import { normKey } from '@odhaczka/core';
import { useState } from 'react';
import type { CheckExtra, CheckStatus } from '../../data/shopping';
import { itemLine, quantityLabel } from '../../data/lists';
import type { DraftItem, Resolution } from '../../data/products';
import type { ListItemRow } from '../../db/types';
import { BottomSheet } from '../BottomSheet';
import { PreviewRow } from '../draft/DraftPreview';
import { asksAbout, useDraft } from '../draft/useDraft';
import { cardCls, inputCls, primaryBtn, secondaryBtn } from '../ui/classes';
import { IconCheck, IconReorder } from './icons';
import { STATUS_LABEL, timeLabel } from './status';
import { SuggestionBanner } from './SuggestionBanner';

interface Props {
  item: ListItemRow | null;
  open: boolean;
  onClose: () => void;
  onCheck: (status: CheckStatus, extra?: CheckExtra) => void;
  onUncheck: () => void;
  onNote: (note: string | null) => void;
  /** Nowa nazwa, ilość i uwagi (z decyzją „to samo?”, gdy nazwa przypomina inny produkt). */
  onEdit: (item: DraftItem, resolution?: Resolution) => void;
  onRemove: () => void;
  /** Brak = przestawianie niedostępne (lista bez sklepu albo pozycja bez produktu). */
  onReorder?: () => void;
  /** Miejsce pozycji na trasie zgadnięte przez AI. */
  guessed?: boolean;
  /** Propozycja zamiennika (null = wycofanie) — zwykle z domu, dla pozycji „brak”. */
  onSuggest: (text: string | null) => void;
  onAcceptSuggestion: () => void;
  onRejectSuggestion: () => void;
  /** Mój identyfikator — moje odhaczenia i propozycje opisujemy bez imienia. */
  me: string | null;
  /** Imię domownika do opisów („Magda”). */
  nameOf: (userId: string) => string;
  /** Ceny pozycji z paragonów (zapłacona, ostatnia, link do historii). */
  price?: React.ReactNode;
}

/** Wszystkie opcje pozycji: statusy, częściowo, zamiennik, notatka, edycja, przywrócenie, przestawienie, usunięcie. */
export function ItemSheet({ item, open, onClose, ...handlers }: Props) {
  return (
    <BottomSheet open={open} onClose={onClose} title={item?.name}>
      {item && (
        // Klucz resetuje podformularz przy zmianie pozycji.
        <SheetBody key={item.id} item={item} {...handlers} />
      )}
    </BottomSheet>
  );
}

type Mode = 'menu' | 'partial' | 'substitute' | 'note' | 'suggest' | 'edit';

function SheetBody({
  item,
  onCheck,
  onUncheck,
  onNote,
  onEdit,
  onRemove,
  onReorder,
  guessed = false,
  onSuggest,
  onAcceptSuggestion,
  onRejectSuggestion,
  me,
  nameOf,
  price,
}: Omit<Props, 'open' | 'onClose' | 'item'> & { item: ListItemRow }) {
  const [mode, setMode] = useState<Mode>('menu');
  const checked = item.status !== 'pending';
  const qty = quantityLabel(item);
  // Częściowo = wziąłem mniej sztuk niż na liście; przy 1 sztuce to po prostu „brak” albo „w koszyku”.
  const maxPartial = item.qty !== null ? Math.ceil(item.qty) - 1 : null;
  const canPartial = maxPartial === null || maxPartial >= 1;

  if (mode === 'partial') return <PartialForm item={item} max={maxPartial} onBack={() => setMode('menu')} onSave={(n) => onCheck('partial', { partialQty: n })} />;
  if (mode === 'substitute')
    return (
      <TextForm
        label="Co zamiast tego?"
        placeholder="np. mleko 2% innej marki"
        initial={item.substitute ?? ''}
        submit="Zapisz zamiennik"
        onBack={() => setMode('menu')}
        onSave={(text) => onCheck('substitute', { substitute: text || null })}
      />
    );
  if (mode === 'suggest')
    return (
      <TextForm
        label="Zaproponuj zamiennik — osoba w sklepie zobaczy go przy pozycji"
        placeholder="np. skyr naturalny"
        initial={item.suggestion ?? ''}
        submit="Wyślij propozycję"
        onBack={() => setMode('menu')}
        onSave={(text) => onSuggest(text || null)}
      />
    );
  if (mode === 'edit') return <EditForm item={item} onBack={() => setMode('menu')} onSave={onEdit} />;
  if (mode === 'note')
    return (
      <TextForm
        label="Notatka"
        placeholder="np. tylko bez laktozy"
        initial={item.note ?? ''}
        submit="Zapisz notatkę"
        onBack={() => setMode('menu')}
        onSave={(text) => onNote(text || null)}
      />
    );

  return (
    <div className="-mt-1 flex flex-col gap-2">
      {(qty || checked) && <p className="-mt-2 mb-1 text-sm text-stone-500">{[qty, checked && statusLine(item, me, nameOf)].filter(Boolean).join(' · ')}</p>}
      {price}
      {guessed && !checked && (
        <p className="-mt-1 mb-1 text-sm text-stone-500">
          <span aria-hidden className="text-violet-500 dark:text-violet-300">
            ✦
          </span>{' '}
          Miejsce zgadnięte — poprawi się po odhaczeniu
        </p>
      )}
      {item.suggestion && (
        <SuggestionBanner
          by={item.suggestedBy && item.suggestedBy === me ? null : item.suggestedBy ? nameOf(item.suggestedBy) : 'Ktoś'}
          text={item.suggestion}
          onAccept={onAcceptSuggestion}
          onReject={onRejectSuggestion}
        />
      )}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onCheck('cart')}
          className={`flex min-h-14 items-center justify-center gap-2 rounded-xl bg-brand-700 font-semibold text-white active:bg-brand-800 ${
            item.status === 'cart' ? 'ring-4 ring-brand-500/40' : ''
          }`}
        >
          <IconCheck /> W koszyku
        </button>
        <button
          type="button"
          onClick={() => onCheck('missing')}
          className={`flex min-h-14 items-center justify-center rounded-xl bg-miss-500 font-semibold text-white active:bg-miss-600 ${
            item.status === 'missing' ? 'ring-4 ring-miss-500/40' : ''
          }`}
        >
          Brak
        </button>
      </div>
      {canPartial && (
        <Action
          onClick={() => setMode('partial')}
          active={item.status === 'partial'}
          hint={item.status === 'partial' && item.partialQty !== null ? `${item.partialQty}${item.qty !== null ? ` z ${item.qty}` : ''}` : 'np. 1 z 2'}
        >
          Częściowo…
        </Action>
      )}
      <Action onClick={() => setMode('substitute')} active={item.status === 'substitute'} hint={item.substitute ?? 'coś innego zamiast tego'}>
        Zamiennik…
      </Action>
      {item.status !== 'cart' && item.status !== 'substitute' && (
        <Action onClick={() => setMode('suggest')} hint={item.suggestion ? `teraz: ${item.suggestion}` : 'np. z domu, gdy czegoś brakuje'}>
          Zaproponuj zamiennik…
        </Action>
      )}
      <Action onClick={() => setMode('note')} hint={item.note ?? undefined}>
        Notatka…
      </Action>
      <Action onClick={() => setMode('edit')} hint="nazwa, ilość i uwagi">
        Edytuj…
      </Action>
      {checked && <Action onClick={onUncheck}>Przywróć do kupienia</Action>}
      {onReorder && (
        <Action onClick={onReorder} hint="przeciągnij na właściwe miejsce w tym sklepie">
          <span className="flex items-center gap-2">
            <IconReorder /> Przestaw na liście
          </span>
        </Action>
      )}
      <Action onClick={onRemove} tone="danger">
        Usuń z listy
      </Action>
    </div>
  );
}

/** „W koszyku · Magda · 14:05” — bez imienia, gdy to moje odhaczenie. */
function statusLine(item: ListItemRow, me: string | null, nameOf: (userId: string) => string): string {
  const label = STATUS_LABEL[item.status];
  const parts = [label.charAt(0).toLocaleUpperCase('pl-PL') + label.slice(1)];
  if (item.checkedBy && item.checkedBy !== me) parts.push(nameOf(item.checkedBy));
  if (item.checkedMs) parts.push(timeLabel(item.checkedMs));
  return parts.join(' · ');
}

interface ActionProps {
  onClick: () => void;
  children: React.ReactNode;
  hint?: string | undefined;
  active?: boolean;
  tone?: 'default' | 'danger';
}

function Action({ onClick, children, hint, active = false, tone = 'default' }: ActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex min-h-12 w-full flex-col items-start justify-center rounded-xl px-3 py-2 text-left active:bg-stone-100 dark:active:bg-stone-800 ${
        active ? 'bg-stone-100 dark:bg-stone-800' : ''
      } ${tone === 'danger' ? 'text-red-600 dark:text-red-400' : ''}`}
    >
      <span className="font-medium">{children}</span>
      {hint && <span className="truncate text-sm text-stone-500">{hint}</span>}
    </button>
  );
}

function PartialForm({ item, max, onBack, onSave }: { item: ListItemRow; max: number | null; onBack: () => void; onSave: (n: number) => void }) {
  const [n, setN] = useState(() => Math.max(1, Math.min(item.partialQty ?? 1, max ?? Infinity)));
  const step = (d: number) => setN((v) => Math.max(1, Math.min(v + d, max ?? Infinity)));
  const btn =
    'flex size-14 items-center justify-center rounded-full border border-stone-300 text-2xl font-semibold active:bg-stone-100 disabled:opacity-30 dark:border-stone-700 dark:active:bg-stone-800';
  return (
    <div className="flex flex-col gap-4">
      <p className="text-center text-stone-600 dark:text-stone-400">Ile sztuk w koszyku?</p>
      <div className="flex items-center justify-center gap-6">
        <button type="button" className={btn} onClick={() => step(-1)} disabled={n <= 1} aria-label="Mniej">
          −
        </button>
        <span className="min-w-20 text-center text-4xl font-bold tabular-nums">
          {n}
          {item.qty !== null && <span className="text-xl font-medium text-stone-500"> z {item.qty}</span>}
        </span>
        <button type="button" className={btn} onClick={() => step(1)} disabled={max !== null && n >= max} aria-label="Więcej">
          +
        </button>
      </div>
      <button type="button" className={primaryBtn} onClick={() => onSave(n)}>
        Zapisz: {n}
        {item.qty !== null ? ` z ${item.qty}` : ''}
      </button>
      <button type="button" className={secondaryBtn} onClick={onBack}>
        Wstecz
      </button>
    </div>
  );
}

interface TextFormProps {
  label: string;
  placeholder: string;
  initial: string;
  submit: string;
  onBack: () => void;
  onSave: (text: string) => void;
}

function TextForm({ label, placeholder, initial, submit, onBack, onSave }: TextFormProps) {
  const [text, setText] = useState(initial);
  const save = (e: { preventDefault(): void }) => {
    e.preventDefault();
    onSave(text.trim());
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-stone-600 dark:text-stone-400">{label}</span>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} enterKeyHint="done" className={inputCls} />
      </label>
      <button type="submit" className={primaryBtn}>
        {submit}
      </button>
      <button type="button" className={secondaryBtn} onClick={onBack}>
        Wstecz
      </button>
    </form>
  );
}

/**
 * Edycja pozycji jedną linią, tak jak w SMS-ie („mleko 2% x2 (bez laktozy)”) — ten sam parser co przy wklejaniu.
 * Inna nazwa to inny produkt: podgląd pokazuje „znany/nowy” i pyta „to samo?” jak przy dopisywaniu.
 */
function EditForm({ item, onBack, onSave }: { item: ListItemRow; onBack: () => void; onSave: (item: DraftItem, resolution?: Resolution) => void }) {
  const [initial] = useState(() => itemLine(item));
  const [text, setText] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const draft = useDraft(text, true);
  const changed = text.trim() !== initial.trim();
  // Ta sama nazwa zostawia produkt pozycji (editItem), więc nie ma o co pytać.
  const sameName = (d: DraftItem) => item.productId !== null && d.key === normKey(item.name);
  const found = draft.items[0];
  const preview = found && sameName(found) ? { ...found, exact: item.productId, candidates: [] } : found;

  const save = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!changed) return onBack();
    const { items, resolutions } = await draft.finalize();
    const next = items[0];
    if (!next) return setError('Wpisz nazwę produktu.');
    onSave(next, sameName(next) ? undefined : resolutions[0]);
  };

  return (
    <form onSubmit={save} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-stone-600 dark:text-stone-400">Nazwa, ilość i uwagi — jak w SMS-ie</span>
        <input
          autoFocus
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setError(null);
          }}
          placeholder="np. mleko 2% x2 (bez laktozy)"
          enterKeyHint="done"
          className={inputCls}
        />
      </label>
      {changed && preview && (
        <ul className={cardCls}>
          <PreviewRow item={preview} answer={draft.answerFor(preview)} onAnswer={(a) => draft.answer(preview.key, a)} />
        </ul>
      )}
      {changed && preview && asksAbout(preview) && !draft.answerFor(preview) && (
        <p className="px-1 text-xs text-stone-500">Bez odpowiedzi zapiszę jako nowy produkt.</p>
      )}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <button type="submit" className={primaryBtn}>
        Zapisz
      </button>
      <button type="button" className={secondaryBtn} onClick={onBack}>
        Wstecz
      </button>
    </form>
  );
}
